import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Card, CardHeader, Button, Modal, Field, Input, Select, Table, THead, TBody,
  TR, TH, TD, StatusBadge, EmptyState, ErrorNotice, Loading, CopyButton,
} from '../../components/ui/index.js';
import { useResource, useMutation } from '../../hooks/useApi.js';
import { useToast } from '../../components/ToastProvider.jsx';
import { http } from '../../lib/api.js';
import { formatMoney, formatNumber, toMinorUnits } from '../../lib/format.js';
import { useAuth } from '../../lib/auth.jsx';
import { Link2 } from 'lucide-react';
import { PageHero } from '../../components/PageHero.jsx';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY', 'SGD', 'AED', 'NZD'];

/** PL-A: one link can collect many payments — each open mints a fresh one. */
export function PaymentLinksPage() {
  const { merchant } = useAuth();
  const toast = useToast();
  /** Opened straight from the top bar's + button. */
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get('new') === '1');
  const [form, setForm] = useState({ title: '', description: '', amount: '', currency: merchant?.defaultCurrency ?? 'USD', maxUses: '' });
  const [amountError, setAmountError] = useState(null);

  const { data, error, loading, refetch } = useResource('/dashboard/payment-links');

  const create = useMutation(async () => {
    const amount = toMinorUnits(form.amount, form.currency);
    if (amount == null || amount <= 0) { setAmountError('Enter an amount like 10.50'); return; }
    setAmountError(null);

    await http.post('/dashboard/payment-links', {
      title: form.title,
      ...(form.description ? { description: form.description } : {}),
      amount,
      currency: form.currency,
      ...(form.maxUses ? { maxUses: Number(form.maxUses) } : {}),
    });
    toast.success('Payment link created');
    setOpen(false);
    setForm({ title: '', description: '', amount: '', currency: merchant?.defaultCurrency ?? 'USD', maxUses: '' });
    refetch();
  });

  const toggle = useMutation(async (link) => {
    const next = link.status === 'active' ? 'inactive' : 'active';
    await http.patch(`/dashboard/payment-links/${link.id}`, { status: next });
    toast.success(next === 'active' ? 'Link reactivated' : 'Link deactivated');
    refetch();
  });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <>
      <PageHero
        icon={Link2}
        chip="Workspace"
        title="Payment links"
        blurb="A reusable link that creates a fresh payment each time someone opens it."
        actions={<Button onClick={() => setOpen(true)}>Create link</Button>}
      />

      <Card>
        <CardHeader title="Your links" />
        {loading && <Loading label="Loading links…" />}
        {error && <ErrorNotice error={error} onRetry={refetch} className="m-4" />}

        {!loading && !error && data?.length === 0 && (
          <EmptyState
            title="No payment links yet"
            blurb="Useful for a fixed-price item you sell repeatedly — share one URL and collect many payments."
            action={<Button onClick={() => setOpen(true)}>Create your first link</Button>}
          />
        )}

        {!loading && !error && data?.length > 0 && (
          <Table>
            <THead>
              <TR><TH>Title</TH><TH align="right">Amount</TH><TH align="right">Uses</TH><TH>Status</TH><TH>Link</TH><TH align="right">Actions</TH></TR>
            </THead>
            <TBody>
              {data.map((link) => (
                <TR key={link.id}>
                  <TD>
                    <div className="font-medium text-ink">{link.title}</div>
                    {link.description && <div className="max-w-xs truncate text-xs text-muted">{link.description}</div>}
                  </TD>
                  <TD align="right" className="tabular font-medium">{formatMoney(link.amount, link.currency)}</TD>
                  <TD align="right" className="tabular text-sm">
                    {formatNumber(link.useCount)}{link.maxUses ? ` / ${link.maxUses}` : ''}
                  </TD>
                  <TD><StatusBadge status={link.status === 'active' ? 'active' : 'disabled'} size="sm" /></TD>
                  {/* PL-C: one-click copy. */}
                  <TD><CopyButton value={link.url} label="Copy URL" /></TD>
                  <TD align="right">
                    <Button size="sm" variant="ghost" loading={toggle.loading} onClick={() => toggle.mutate(link).catch(() => {})}>
                      {link.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Modal
        open={open}
        onClose={() => { setOpen(false); if (params.get('new')) setParams({}, { replace: true }); }}
        title="Create a payment link"
        blurb="Anyone with the URL can pay. The amount is fixed."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={create.loading}>Cancel</Button>
            <Button onClick={() => create.mutate().catch(() => {})} loading={create.loading}>Create link</Button>
          </>
        }
      >
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); create.mutate().catch(() => {}); }}>
          {create.error && <ErrorNotice error={create.error} title="Could not create the link" />}

          <Field label="Title" hint="Shown to the customer at checkout." required error={create.error?.fieldErrors?.title}>
            {(props) => <Input {...props} value={form.title} onChange={set('title')} placeholder="Monthly coffee subscription" autoFocus />}
          </Field>

          <div className="grid grid-cols-[1fr_7rem] gap-3">
            <Field label="Amount" required error={amountError}>
              {(props) => <Input {...props} inputMode="decimal" value={form.amount} invalid={Boolean(amountError)} onChange={set('amount')} placeholder="25.00" />}
            </Field>
            <Field label="Currency">
              {(props) => (
                <Select {...props} value={form.currency} onChange={set('currency')}>
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              )}
            </Field>
          </div>

          <Field label="Maximum uses" hint="Leave blank for unlimited.">
            {(props) => <Input {...props} inputMode="numeric" value={form.maxUses} onChange={set('maxUses')} placeholder="Unlimited" />}
          </Field>
        </form>
      </Modal>
    </>
  );
}

export default PaymentLinksPage;
